import { Slot as SlotPrimitive } from 'radix-ui';
import { tv } from 'tailwind-variants';

import { label } from '../typography/Label';

import type { VariantProps } from 'tailwind-variants';

const badge = tv({
  extend: label,
  base: `
    focus-visible:border-ring
    focus-visible:ring-ring/50
    aria-invalid:ring-destructive/20
    dark:aria-invalid:ring-destructive/40
    aria-invalid:border-destructive
    inline-flex
    h-6
    w-fit
    shrink-0
    items-center
    justify-center
    gap-1
    overflow-hidden
    rounded-lg
    border
    px-2
    text-xs
    whitespace-nowrap
    transition-[color,box-shadow]
    focus-visible:ring-[3px]
    [&>svg]:pointer-events-none
    [&>svg]:size-3
  `,
  variants: {
    variant: {
      default: `
        bg-background-secondary
        border-border-tag-default
        text-primary
        [a&]:hover:bg-primary/90
      `,
      negative: `
        bg-background-negative-light
        text-content-negative-dark
        border-border-tag-negative
      `,
      positive: `
        text-content-positive-dark
        bg-background-positive-light
        border-border-tag-positive
      `,
      warning: `
        bg-background-warning-light
        text-content-warning-dark
        border-border-tag-warning
      `,
      accent: `
        bg-background-accent-light
        text-content-accent-dark
        border-border-tag-accent
      `,
      option: `
        bg-background-secondary
        border-border-secondary
        text-primary
        rounded-sm
        [a&]:hover:bg-primary/90
      `,
      /**
       * @deprecated
       */
      pending: `
        bg-background-secondary
        border-border-tag-default
        text-content
        [a&]:hover:bg-primary/90
      `,
      /**
       * @deprecated
       */
      danger: `
        bg-background-negative-light
        text-content-negative-dark
        border-border-tag-negative
      `,
      /**
       * @deprecated
       */
      success: `
        bg-background-positive-light
        text-content-positive-dark
        border-border-tag-positive
      `,
      /**
       * @deprecated
       */
      review: `
        bg-background-accent-light
        text-content-accent-dark
        border-border-tag-accent
      `,
      /**
       * @deprecated
       */
      complete: `
        bg-background-positive-light
        text-content-positive-dark
        border-border-tag-positive
      `,
    },
    wrap: {
      true: `
        h-auto
        py-0.5
        text-left
        break-all
        whitespace-normal
      `,
    },
  },
  defaultVariants: {
    size: 'sm',
    variant: 'default',
    weight: 'medium',
    wrap: false,
  },
});

export interface BadgeProps extends React.ComponentProps<'span'> {
  variant?: VariantProps<typeof badge>['variant'];
  asChild?: boolean;
  /**
   * Let the label run onto as many lines as it needs, instead of staying on the
   * single fixed-height line a badge is by default.
   *
   * This is what makes a badge usable for a long machine identifier inside a table:
   * a nowrap badge in a nowrap cell means the longest value in the column sets the
   * column's minimum width, so it pushes the rest of the row past the edge of its
   * container. Wrapping drops that minimum width to one character and keeps the
   * table inside the container at any width. The vertical padding and left
   * alignment come with it, because a badge that no longer has a fixed height
   * needs its own padding, and lines of a multi-line label read from the left
   * rather than centred.
   */
  wrap?: VariantProps<typeof badge>['wrap'];
}

function Badge({ className, variant, wrap, asChild = false, ...props }: BadgeProps): React.ReactElement {
  const Comp = asChild ? SlotPrimitive.Slot : 'span';

  return <Comp className={badge({ variant, wrap, className })} data-slot="badge" {...props} />;
}

export { badge, Badge };
